import { Inject, Injectable } from '@angular/core';
import {
    API_BASE_URL,
    ControlCentreApiClient,
    IWorklistItemDto,
    PatchOrderRequest,
    PatchPriorRequest,
    PatchProcedureRequest,
    PostPriorRequest,
    PriorDto,
    StatusType,
    UrgencyType,
    WorklistItemDataSourceType,
    WorklistItemDetailsDto
} from '../api-module/control-centre-api-client';
import { DateTime } from 'luxon';
import { firstValueFrom, Observable, of, Subject } from 'rxjs';
import { catchError, retry, timeout } from 'rxjs/operators';
import { Constants } from '../models/constants';
import { ConfigService } from '../services/config.service';
import { SignalRService } from '../services/signal-r.service';
import { HubConnection } from '@microsoft/signalr';
import { SnackbarService } from '../services/snackbar.sevice';
import { AuthenticationService } from '../authentication/services/authentication.service';
import { HttpClient } from '@angular/common/http';
import { PostPriorReportRequest } from '@app/models/post-prior-report-request.model';

@Injectable({
    providedIn: 'root'
})
export class WorklistService {
    constructor(
        private readonly controlCentreApiClient: ControlCentreApiClient,
        private readonly configService: ConfigService,
        private readonly signalRService: SignalRService,
        private readonly snackbarService: SnackbarService,
        private readonly authenticationService: AuthenticationService,
        private readonly httpClient: HttpClient,
        @Inject(API_BASE_URL) private readonly baseUrl: string
    ) {}

    ordersCount$ = new Subject<number>();

    query(
        orgCode: string,
        siteIds: number[],
        fromTime: DateTime,
        toTime: DateTime,
        statusTypes: StatusType[],
        urgencyTypes: UrgencyType[],
        includeUnlinkedOrders: boolean,
        includeReferralList: boolean = false,
        modalityTypes: string[] = []
    ): Observable<IWorklistItemDto[]> {
        return this.controlCentreApiClient
            .worklistItems_Query(
                orgCode,
                siteIds.map(id => (id ? String(id) : '')),
                fromTime.toISO(),
                toTime.toISO(),
                null,
                statusTypes,
                urgencyTypes,
                includeReferralList,
                includeUnlinkedOrders,
                modalityTypes
            )
            .pipe(timeout(Constants.HttpTimeoutMseconds), retry(Constants.HttpRetry))
            .pipe(
                catchError(error => {
                    if (error.status === 403) {
                        this.snackbarService.openSnackBar(
                            `You do not have permission to view this worklist(orgCode: ${orgCode}, siteIds: ${siteIds})`,
                            SnackbarService.SNACK_BAR_CLASS_ALERT
                        );
                    } else {
                        this.snackbarService.openSnackBar(Constants.UnableToContactServerMessage, SnackbarService.SNACK_BAR_CLASS_ALERT);
                    }

                    return of([] as IWorklistItemDto[]); // Not to rethrow exception to avoid breaking subscriptions.
                })
            );
    }

    get(id: string, type: WorklistItemDataSourceType): Observable<WorklistItemDetailsDto> {
        let stream: Observable<WorklistItemDetailsDto>;
        switch (type) {
            case WorklistItemDataSourceType.UnlinkedOrder:
                stream = this.controlCentreApiClient.worklistItems_GetUnlinkedOrder(id);
                break;

            case WorklistItemDataSourceType.Order:
                stream = this.controlCentreApiClient.worklistItems_GetOrder(id);
                break;

            default:
                throw Error(`Unexpected type: [${type}]`);
        }

        return stream.pipe(timeout(Constants.HttpTimeoutMseconds), retry(Constants.HttpRetry)).pipe(
            catchError(error => {
                if (error.status === 403) {
                    this.snackbarService.openSnackBar(
                        `You do not have permission to view this worklist item (${id})`,
                        SnackbarService.SNACK_BAR_CLASS_ALERT
                    );
                } else {
                    this.snackbarService.openSnackBar(Constants.UnableToContactServerMessage, SnackbarService.SNACK_BAR_CLASS_ALERT);
                }

                return of(null); // Not to rethrow exception to avoid breaking subscriptions.
            })
        );
    }

    async patchOrder(id: string, request: PatchOrderRequest): Promise<void> {
        // The requestingUserDisplayName should always be the current login's display name.
        // So it should be here and shouldn't be overwritten by any code.
        // It'll be even better not to use payload but token to pass the display name.
        request.requestingUserDisplayName = this.authenticationService.getCurrentUser()?.username;
        await this.controlCentreApiClient.worklistItems_PatchOrder(id, request).toPromise();
    }

    async patchProcedure(orderId: string, procedureId: number, request: PatchProcedureRequest): Promise<void> {
        await this.controlCentreApiClient.worklistItems_PatchProcedure(orderId, procedureId, request).toPromise();
    }

    async createPrior(orderId: string, request: PostPriorRequest): Promise<PriorDto> {
        return await this.controlCentreApiClient.worklistItems_PostOrderPrior(orderId, request).toPromise();
    }

    async patchPrior(orderId: string, priorId: number, request: PatchPriorRequest): Promise<PriorDto> {
        return await this.controlCentreApiClient.worklistItems_PatchOrderPrior(orderId, priorId, request).toPromise();
    }

    async deletePrior(orderId: string, priorId: number, radiographerName?: string): Promise<void> {
        await this.controlCentreApiClient.worklistItems_DeleteOrderPrior(orderId, priorId, radiographerName).toPromise();
    }

    uploadScanDoc(accessionNumber: string, formData: FormData) {
        const fileUploadUrl = `/control-centre-api/v1/orders/${accessionNumber}/scanned-documents`;
        return this.httpClient.post(this.baseUrl + fileUploadUrl, formData, {
            reportProgress: true,
            observe: 'events'
        });
    }

    async getFileUrl(accessionNumber: string, documentName: string): Promise<string> {
        return await firstValueFrom(this.controlCentreApiClient.scannedDocuments_GetScannedDocumentUrl(accessionNumber, documentName));
    }

    uploadPriorReport(accessionNumber: string, postPriorReportRequest: PostPriorReportRequest) {
        const fileUploadUrl = `/control-centre-api/v1/orders/${accessionNumber}/prior-reports`;
        return this.httpClient.post(this.baseUrl + fileUploadUrl, postPriorReportRequest.formData, {
            reportProgress: true,
            observe: 'events'
        });
    }

    async startSignalRConnection(
        orgCode: string,
        siteIds: number[],
        onUpsertFn: (_: IWorklistItemDto) => void,
        onDeleteFn: (_: string) => void,
        onMoveFn: (_: string, __: string, ___?: number) => void
    ): Promise<HubConnection> {
        const hubConnection = this.signalRService.createHubConnection(
            this.configService.getSettings().apiBaseUrl + '/api/hubs/work-list-items',
            () => this.subscribeWorklist(hubConnection, orgCode, siteIds)
        );
        hubConnection.on('OnUpdateWorkListItem', onUpsertFn);
        hubConnection.on('OnDeleteWorkListItem', onDeleteFn);
        hubConnection.on('OnMoveWorkListItem', onMoveFn);

        await hubConnection.start();

        await this.subscribeWorklist(hubConnection, orgCode, siteIds);

        return hubConnection;
    }

    private async subscribeWorklist(hubConnection: HubConnection, orgCode: string, siteIds: number[]) {
        await hubConnection.invoke('SubscribeWorkList', orgCode, siteIds);
    }
}
